interface RepeatUntilOptions<TResultType, TErrorType> {
  initialInterval?: number
  maxInterval?: number
  timeout?: number | null
  exponential?: boolean
  failurePredicateFn?: (
    result: TResultType
  ) => TErrorType | string | undefined | null
  failOnCatch?: boolean
}

export interface RepeatUntilHandle<TDerivedResultType> {
  promise: Promise<TDerivedResultType>
  cancel: () => void
}

export function repeatUntil<TResultType, TDerivedResultType, TErrorType>(
  repeatFn: () => Promise<TResultType>,
  predicateFn: (result: TResultType) => boolean,
  thenFn: (result: TResultType) => Promise<TDerivedResultType>,
  options: RepeatUntilOptions<TResultType, TErrorType> = {}
): RepeatUntilHandle<TDerivedResultType> {
  const {
    initialInterval = 400,
    maxInterval = 10000,
    timeout = null,
    exponential = false,
    failOnCatch = false
  } = options

  let stopped = false
  let currentInterval = initialInterval
  let timeoutTimer: NodeJS.Timeout | undefined
  let retryTimer: NodeJS.Timeout | undefined

  const cleanup = () => {
    stopped = true
    if (timeoutTimer) clearTimeout(timeoutTimer)
    if (retryTimer) clearTimeout(retryTimer)
  }

  const promise = new Promise<TDerivedResultType>((resolve, reject) => {
    if (timeout) {
      timeoutTimer = setTimeout(() => {
        cleanup()
        reject(new Error(`timed out after ${timeout} ms`))
      }, timeout)
    }

    const tryUntil = async () => {
      if (stopped) return

      try {
        const result = await repeatFn()

        if (stopped) return

        if (predicateFn(result)) {
          cleanup()
          const finalResult = await thenFn(result)
          resolve(finalResult)
          return
        }

        const failureReason = options.failurePredicateFn?.(result)
        if (failureReason) {
          cleanup()
          reject(failureReason)
          return
        }
      } catch (e) {
        if (failOnCatch) {
          cleanup()
          reject(e)
          return
        }
      }

      if (!stopped) {
        if (exponential) {
          currentInterval = Math.min(currentInterval * 2, maxInterval)
        }
        retryTimer = setTimeout(tryUntil, currentInterval)
      }
    }

    tryUntil()
  })

  return { promise, cancel: cleanup }
}
